/**
 * The vulnerabilities a scan matched against the Wordfence feed, one entry
 * per installed plugin, theme or WordPress core (`groupVulns`), worst first.
 *
 * Why grouped: the feed has one record per hole, and an old plugin can match
 * six of them. Listed record by record, "Fluent Forms 5.1.11" stood on six
 * rows scattered by severity, and the page did not say that one update closes
 * them all. Now each software is one entry: its installed version, how many
 * records match it, the worst of them, and the version to update to (the
 * server's `fixAll`, see `lib/monitoringVulnGroups.ts` for why it is not the
 * highest fixed version among them), with a note when some have no fix yet.
 *
 * A software with one record shows that record in place: its title and links
 * in the row, nothing to open. One with more shows the count and a toggle;
 * opened, each record is listed with its own severity, fixed version and
 * links. The Wordfence record link stays on every shown record, as the feed's
 * licence requires (`VulnParts`). A match made on a version the homepage
 * shows, not the reporter's exact one, is marked "possible" once per entry.
 *
 * A table from the `sm` breakpoint up; on a phone, one block per software.
 * The fix column wraps instead of running off the card's right edge.
 */
import { Fragment, useId, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@wa/ui";
import { vulnSoftware, type SecurityVulnView } from "../../../../lib/monitoringSecurity";
import { groupVulns, type VulnGroup } from "../../../../lib/monitoringVulnGroups";
import { IconBtn, StatusPill } from "../../../ui";
import type { T } from "../words";
import { GroupFix, Links, RecordFix, Severity } from "./VulnParts";

/** The software's name and installed version. */
function Software({ v }: { v: SecurityVulnView }) {
  return (
    <>
      <span className="font-semibold">{vulnSoftware(v)}</span> <span className="crm-mut crm-tnum text-[11.5px]">{v.version}</span>
    </>
  );
}

/** The count of a software's records and the toggle that opens them. */
function Toggle({ group, open, controls, onToggle, t }: { group: VulnGroup; open: boolean; controls: string; onToggle: () => void; t: T }) {
  // Named, so a screen reader's list of buttons says which software each one opens.
  const label = t(open ? "monitoring.security.hideVulns" : "monitoring.security.showVulns", { name: vulnSoftware(group.vulns[0]!) });
  return (
    <span className="flex flex-wrap items-center gap-2">
      <IconBtn small aria-expanded={open} aria-controls={controls} aria-label={label} title={label} onClick={onToggle}>
        <ChevronDown className={cn("size-4 transition-transform duration-200", open && "rotate-180")} strokeWidth={1.8} />
      </IconBtn>
      <span className="crm-tnum font-medium">{t("monitoring.security.vulnCount", { n: group.vulns.length })}</span>
      {group.possible ? <StatusPill tone="mut">{t("monitoring.security.possible")}</StatusPill> : null}
    </span>
  );
}

/** An opened software's records, each with its severity, fix and links. */
function Records({ group, id, t }: { group: VulnGroup; id: string; t: T }) {
  return (
    <ul
      id={id}
      className="grid grid-cols-[minmax(0,1fr)] gap-2.5 rounded-[12px] bg-[var(--crm-sunk)] px-3.5 py-3 [&>li+li]:border-t [&>li+li]:border-[var(--crm-hair)] [&>li+li]:pt-2.5"
    >
      {group.vulns.map((v) => (
        // Fixed side columns: every record is its own grid, and sized to content the pills of
        // "9.8 (Kriitiline)" and "6.4 (Keskmine)" would not line up under each other. On a
        // phone the title takes the full width and the pill and the fix share the next line.
        <li
          key={v.key}
          className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 text-[12.5px] sm:grid-cols-[minmax(0,1fr)_8.5rem_10.5rem] sm:items-start sm:gap-x-4"
        >
          <span className="col-span-2 min-w-0 break-words sm:col-span-1">
            <span className="block">{v.title}</span>
            <Links v={v} t={t} withPossible={false} />
          </span>
          <span>
            <Severity v={v} t={t} />
          </span>
          <span className="sm:pt-0.5">
            <RecordFix v={v} t={t} />
          </span>
        </li>
      ))}
    </ul>
  );
}

export function VulnList({ vulns, t }: { vulns: SecurityVulnView[]; t: T }) {
  const groups = useMemo(() => groupVulns(vulns), [vulns]);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const idBase = useId();
  const toggle = (key: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // The phone list and the table are both in the page; each needs its own ids.
  const recordsId = (where: "phone" | "table", i: number) => `${idBase}-${where}-${i}`;

  return (
    <>
      {/* Phone: one block per software. */}
      <ul className="flex flex-col gap-2.5 sm:hidden">
        {groups.map((g, i) => {
          const head = g.vulns[0]!;
          const isOpen = open.has(g.key);
          return (
            // One column that may shrink: an auto grid column is as wide as its longest word.
            <li key={g.key} className="grid grid-cols-[minmax(0,1fr)] gap-1.5 rounded-[12px] border border-[var(--crm-hair)] px-3.5 py-3 text-[13px]">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 break-words">
                  <Software v={head} />
                </p>
                <span className="shrink-0">
                  <Severity v={head} t={t} />
                </span>
              </div>
              {g.vulns.length === 1 ? (
                <div className="break-words">
                  <p>{head.title}</p>
                  <Links v={head} t={t} />
                </div>
              ) : (
                <Toggle group={g} open={isOpen} controls={recordsId("phone", i)} onToggle={() => toggle(g.key)} t={t} />
              )}
              <p className="text-[12.5px]">
                <GroupFix group={g} t={t} />
              </p>
              {isOpen && g.vulns.length > 1 ? <Records group={g} id={recordsId("phone", i)} t={t} /> : null}
            </li>
          );
        })}
      </ul>

      {/* Scrolls only if a name or title will not wrap; the fix column wraps rather than running off. */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="crm-table">
          <thead>
            <tr>
              <th scope="col">{t("monitoring.security.col.software")}</th>
              <th scope="col">{t("monitoring.security.col.issue")}</th>
              <th scope="col">{t("monitoring.security.col.cvss")}</th>
              <th scope="col">{t("monitoring.security.col.fix")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, i) => {
              const head = g.vulns[0]!;
              const isOpen = open.has(g.key);
              return (
                <Fragment key={g.key}>
                  <tr>
                    <td className="align-top">
                      <span className="block font-semibold">{vulnSoftware(head)}</span>
                      <span className="crm-mut crm-tnum block text-[11.5px]">{head.version}</span>
                    </td>
                    <td className="align-top">
                      {g.vulns.length === 1 ? (
                        <>
                          <span className="block">{head.title}</span>
                          <Links v={head} t={t} />
                        </>
                      ) : (
                        <Toggle group={g} open={isOpen} controls={recordsId("table", i)} onToggle={() => toggle(g.key)} t={t} />
                      )}
                    </td>
                    <td className="align-top">
                      <Severity v={head} t={t} />
                    </td>
                    <td className="min-w-[10rem] align-top">
                      <GroupFix group={g} t={t} />
                    </td>
                  </tr>
                  {isOpen && g.vulns.length > 1 ? (
                    <tr>
                      {/* No divider above: the records belong to the row they open from. */}
                      <td colSpan={4} className="border-t-0 pt-0">
                        <Records group={g} id={recordsId("table", i)} t={t} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {vulns.some((v) => v.possible) ? <p className="crm-mut mt-2 text-[12px]">{t("monitoring.security.possibleHint")}</p> : null}
    </>
  );
}
